import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RouteProp } from '@react-navigation/native';
import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';

import type { RootStackParamList } from '../navigation/stackTypes';
import Header from '../components/Header';
import LoadingButton from '../components/LoadingButton';
import { isSuccessful } from '../services/api-requests';
import { listRfidCards } from '../services/rfid-service';
import { getDefaultPaymentMethod } from '../services/payment-method-service';
import {
  ApiRfidCard,
  STATUS_COLORS,
  STATUS_LABELS,
  formatCardLabel,
  formatRelativeTime,
} from '../models/rfid-card-model';
import { PaymentMethodSummary, formatCardBrand } from '../models/payment-method-model';

type NavigationProp = NativeStackNavigationProp<RootStackParamList, 'MyRfidTags'>;
type RouteProps = RouteProp<RootStackParamList, 'MyRfidTags'>;

const MyRfidTags: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const highlightCardUuid = route.params?.highlightCardUuid;

  const [cards, setCards] = useState<ApiRfidCard[]>([]);
  const [pm, setPm] = useState<PaymentMethodSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  const fetchAll = useCallback(async () => {
    try {
      const [cardsResp, pmResp] = await Promise.all([
        listRfidCards(),
        getDefaultPaymentMethod(),
      ]);
      if (isSuccessful(cardsResp.status) && cardsResp.payload) {
        setCards(cardsResp.payload.cards ?? []);
      }
      if (isSuccessful(pmResp.status)) {
        setPm(pmResp.payload?.default_payment_method ?? null);
      }
    } catch (e) {
      console.error('[MyRfidTags] fetch error:', e);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchAll();
      setLoading(false);
    })();
  }, [fetchAll]);

  // Refresh on every focus so newly-paired tags appear after navigating back
  // from the pairing flow.
  useFocusEffect(
    useCallback(() => {
      fetchAll();
    }, [fetchAll]),
  );

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchAll();
    setRefreshing(false);
  }, [fetchAll]);

  // Per §2 of the contract: hide REVOKED, surface DISABLED with an Enable CTA
  // (handled inside the detail screen), keep ACTIVE and PENDING_CLAIM visible.
  const visibleCards = useMemo(
    () => cards.filter((c) => c.status !== 'REVOKED'),
    [cards],
  );

  const hasActive = visibleCards.some((c) => c.status === 'ACTIVE');
  const showPmBanner = hasActive && !pm;

  const renderCard = (card: ApiRfidCard) => {
    const isHighlighted = highlightCardUuid === card.uuid;
    const statusColor = STATUS_COLORS[card.status];
    const statusLabel = STATUS_LABELS[card.status];

    return (
      <Pressable
        key={card.uuid}
        onPress={() =>
          navigation.navigate('RfidCardSettings', { cardUuid: card.uuid })
        }
        style={({ pressed }) => [
          styles.cardRow,
          isHighlighted && styles.cardHighlighted,
          { opacity: pressed ? 0.85 : 1 },
        ]}
      >
        <View style={styles.cardIcon}>
          <MaterialCommunityIcons
            name="contactless-payment"
            size={26}
            color="#40BFB4"
          />
        </View>
        <View style={styles.cardBody}>
          <View style={styles.cardTopRow}>
            <Text style={styles.cardLabel} numberOfLines={1}>
              {formatCardLabel(card)}
            </Text>
            <View
              style={[styles.statusPill, { backgroundColor: statusColor + '22' }]}
            >
              <View style={[styles.statusDot, { backgroundColor: statusColor }]} />
              <Text style={[styles.statusText, { color: statusColor }]}>
                {statusLabel}
              </Text>
            </View>
          </View>
          <Text style={styles.cardSub} numberOfLines={1}>
            {card.status === 'PENDING_CLAIM'
              ? 'Tap the card on a charger to finish setup'
              : `Last used ${formatRelativeTime(card.last_used_at)}`}
          </Text>
        </View>
        <MaterialIcons name="chevron-right" size={22} color="#8D8E90" />
      </Pressable>
    );
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      {Platform.OS !== 'web' && <Header headerText="My RFID Tags" />}

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor="#40BFB4"
          />
        }
      >
        <View style={styles.headerCard}>
          <View style={styles.headerCopy}>
            <Text style={styles.headerTitle}>Tap & charge</Text>
            <Text style={styles.headerSubtitle}>
              Pair an RFID tag once, then tap any Meander charger to start a
              session. We charge your default payment method automatically.
            </Text>
          </View>
          <LoadingButton
            onPress={() => navigation.navigate('AddRfidTag')}
            backgroundColor="#40BFB4"
            style={styles.addButton}
          >
            Add a tag
          </LoadingButton>
        </View>

        {showPmBanner && (
          <Pressable
            onPress={() => navigation.navigate('PaymentMethods')}
            style={({ pressed }) => [
              styles.banner,
              { opacity: pressed ? 0.85 : 1 },
            ]}
          >
            <MaterialIcons name="warning" size={20} color="#F59E0B" />
            <View style={{ flex: 1 }}>
              <Text style={styles.bannerTitle}>Add a payment method</Text>
              <Text style={styles.bannerText}>
                Required for tap-to-charge. Your tags are active but won&apos;t
                authorize until a card is on file.
              </Text>
            </View>
            <MaterialIcons name="chevron-right" size={22} color="#FFE9C7" />
          </Pressable>
        )}

        {pm && (
          <View style={styles.pmRow}>
            <MaterialCommunityIcons
              name="credit-card-check-outline"
              size={18}
              color="#10B981"
            />
            <Text style={styles.pmText}>
              Default: {formatCardBrand(pm.brand)} ••{pm.last4}
            </Text>
            <Pressable
              onPress={() => navigation.navigate('PaymentMethods')}
              hitSlop={8}
            >
              <Text style={styles.pmLink}>Manage</Text>
            </Pressable>
          </View>
        )}

        <Text style={styles.sectionTitle}>Your tags</Text>

        {loading ? (
          <View style={styles.centered}>
            <ActivityIndicator size="large" color="#40BFB4" />
          </View>
        ) : visibleCards.length === 0 ? (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons
              name="contactless-payment-circle-outline"
              size={56}
              color="#8D8E90"
            />
            <Text style={styles.emptyTitle}>No RFID tags yet</Text>
            <Text style={styles.emptySubtitle}>
              Add a tag and tap it on any Meander charger to pair it with your
              account.
            </Text>
          </View>
        ) : (
          <View style={styles.list}>{visibleCards.map(renderCard)}</View>
        )}

        <View style={{ height: 32 }} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#1A1D21' },
  scroll: { flex: 1 },
  scrollContent: { paddingHorizontal: 16, paddingBottom: 40 },
  headerCard: {
    backgroundColor: '#292D33',
    borderRadius: 14,
    padding: 16,
    marginTop: 12,
    marginBottom: 16,
  },
  headerCopy: { marginBottom: 12 },
  headerTitle: {
    color: '#FFFFFF',
    fontSize: 18,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '700',
    marginBottom: 4,
  },
  headerSubtitle: {
    color: '#8D8E90',
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Regular',
    lineHeight: 18,
  },
  addButton: { borderRadius: 12, paddingVertical: 4 },
  banner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    backgroundColor: '#3A2E14',
    borderRadius: 12,
    padding: 14,
    borderLeftWidth: 3,
    borderLeftColor: '#F59E0B',
    marginBottom: 16,
  },
  bannerTitle: {
    color: '#FFE9C7',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '700',
    marginBottom: 2,
  },
  bannerText: {
    color: '#FFE9C7',
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
    lineHeight: 16,
  },
  pmRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#1F2933',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 16,
  },
  pmText: {
    flex: 1,
    color: '#FFFFFF',
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Regular',
  },
  pmLink: {
    color: '#40BFB4',
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
  },
  sectionTitle: {
    color: '#8D8E90',
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 8,
  },
  list: { gap: 10 },
  cardRow: {
    backgroundColor: '#292D33',
    borderRadius: 12,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  cardHighlighted: { borderWidth: 1, borderColor: '#40BFB4' },
  cardIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#40BFB420',
  },
  cardBody: { flex: 1 },
  cardTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  cardLabel: {
    color: '#FFFFFF',
    fontSize: 15,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
    flexShrink: 1,
  },
  cardSub: {
    color: '#8D8E90',
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
  },
  statusPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  statusDot: { width: 6, height: 6, borderRadius: 3 },
  statusText: {
    fontSize: 11,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '700',
  },
  centered: { paddingVertical: 40, alignItems: 'center' },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 24,
  },
  emptyTitle: {
    color: '#FFFFFF',
    fontSize: 17,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
    marginTop: 12,
    marginBottom: 6,
  },
  emptySubtitle: {
    color: '#8D8E90',
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Regular',
    textAlign: 'center',
    lineHeight: 18,
  },
});

export default MyRfidTags;
